import type { CSSProperties } from 'react'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'

// ─── Config ───────────────────────────────────────────────────────────────────

const ACTIVITY_OPTIONS = [
  { value: 'sedentary',  label: 'Sedentary',   desc: 'Little or no exercise' },
  { value: 'light',      label: 'Light',        desc: '1–3 days/week' },
  { value: 'moderate',   label: 'Moderate',     desc: '3–5 days/week' },
  { value: 'active',     label: 'Active',       desc: '6–7 days/week' },
  { value: 'very_active',label: 'Very active',  desc: 'Hard exercise, physical job' },
]

const GOAL_OPTIONS = [
  { value: -750, label: 'Aggressive cut',  desc: '-750 kcal/day · ~0.7 kg/wk loss' },
  { value: -500, label: 'Moderate cut',    desc: '-500 kcal/day · ~0.5 kg/wk loss' },
  { value: -250, label: 'Light cut',       desc: '-250 kcal/day · ~0.25 kg/wk loss' },
  { value:    0, label: 'Maintain',        desc: 'TDEE · 0 kg/wk change' },
  { value:  250, label: 'Light bulk',      desc: '+250 kcal/day · ~0.25 kg/wk gain' },
  { value:  500, label: 'Lean bulk',       desc: '+500 kcal/day · ~0.5 kg/wk gain' },
]

const DIET_OPTIONS = [
  { value: 'balanced',     label: 'Balanced',      desc: 'P 30% · C 40% · F 30%' },
  { value: 'high_protein', label: 'High protein',  desc: 'P 35% · C 35% · F 30%' },
  { value: 'low_carb',     label: 'Low carb',      desc: 'P 30% · C 20% · F 50%' },
  { value: 'keto',         label: 'Keto',           desc: 'P 25% · C 5% · F 70%' },
  { value: 'high_carb',    label: 'High carb',     desc: 'P 20% · C 60% · F 20%' },
]

const MANUAL_PRESETS = [
  {
    value: 'psmf',
    label: 'PSMF',
    desc: 'Protein-sparing modified fast — protein from body weight, minimal fat & carbs',
  },
]

// ─── Helper components ────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <label style={{ display: 'block', fontSize: '0.78rem', color: '#a1a1aa', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function NumberInput({
  value, onChange, placeholder, min,
}: {
  value: string; onChange: (v: string) => void; placeholder?: string; min?: string
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      style={{
        width: '100%',
        background: '#27272a',
        border: '1px solid #3f3f46',
        borderRadius: 8,
        color: '#fafafa',
        fontSize: '0.9rem',
        padding: '0.55rem 0.75rem',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  )
}

function SegmentedControl({
  options, value, onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div style={{ display: 'flex', background: '#18181b', borderRadius: 8, padding: 2, gap: 2 }}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            flex: 1,
            background: value === o.value ? '#27272a' : 'none',
            border: 'none',
            borderRadius: 6,
            color: value === o.value ? '#fafafa' : '#71717a',
            fontSize: '0.8rem',
            fontWeight: value === o.value ? 600 : 400,
            padding: '0.4rem 0',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function PickerList({
  options, value, onChange, accentColor = '#22d3ee',
}: {
  options: { value: string | number; label: string; desc: string }[]
  value: string | number
  onChange: (v: any) => void
  accentColor?: string
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {options.map((o) => {
        const selected = o.value === value
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: selected ? '#0c1e2e' : '#18181b',
              border: `1px solid ${selected ? accentColor : '#27272a'}`,
              borderRadius: 8,
              color: '#fafafa',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: '0.85rem', fontWeight: selected ? 600 : 400 }}>
              {o.label}
            </span>
            <span style={{ fontSize: '0.72rem', color: '#71717a' }}>{o.desc}</span>
          </button>
        )
      })}
    </div>
  )
}

// ─── Macro preview card ───────────────────────────────────────────────────────

function MacroPreview({
  calories, protein, carbs, fat, label,
}: {
  calories: number; protein: number; carbs: number; fat: number; label?: string
}) {
  return (
    <div
      style={{
        background: '#0f0f11',
        border: '1px solid #27272a',
        borderRadius: 10,
        padding: '0.75rem',
        marginTop: '0.75rem',
      }}
    >
      {label && (
        <div style={{ fontSize: '0.72rem', color: '#71717a', marginBottom: '0.5rem' }}>{label}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem' }}>
        {[
          { label: 'Calories', value: `${calories}`, unit: 'kcal', color: '#fafafa' },
          { label: 'Protein',  value: `${protein}`,  unit: 'g',    color: '#22d3ee' },
          { label: 'Carbs',    value: `${carbs}`,    unit: 'g',    color: '#a78bfa' },
          { label: 'Fat',      value: `${fat}`,      unit: 'g',    color: '#fb923c' },
        ].map(({ label: l, value, unit, color }) => (
          <div key={l} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color }}>
              {value}
              <span style={{ fontSize: '0.6rem', fontWeight: 400, color: '#71717a' }}>{unit}</span>
            </div>
            <div style={{ fontSize: '0.65rem', color: '#71717a' }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const queryClient = useQueryClient()

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile.get(),
  })

  const { data: authUser } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.auth.me(),
  })

  // ── Body stats ──
  const [dob, setDob] = useState('')
  const [sex, setSex] = useState('male')
  const [heightCm, setHeightCm] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [activity, setActivity] = useState('moderate')

  // ── Target mode ──
  const [targetMode, setTargetMode] = useState<'auto' | 'manual'>('auto')

  // ── Auto mode ──
  const [goalDelta, setGoalDelta] = useState<number>(-500)
  const [dietType, setDietType] = useState('balanced')

  // ── Manual mode ──
  const [manualProtein, setManualProtein] = useState('')
  const [manualCarbs, setManualCarbs] = useState('')
  const [manualFat, setManualFat] = useState('')
  const [targetWater, setTargetWater] = useState('2000')

  const [saved, setSaved] = useState(false)

  // ── Populate from saved profile ──
  useEffect(() => {
    if (!profile) return
    if (profile.dob) setDob(profile.dob)
    if (profile.sex) setSex(profile.sex)
    if (profile.height_cm) setHeightCm(String(profile.height_cm))
    if (profile.activity_level) setActivity(profile.activity_level)
    if (profile.goal_kcal_delta != null) setGoalDelta(profile.goal_kcal_delta)
    if (profile.diet_type) setDietType(profile.diet_type)
    if (profile.target_water_ml) setTargetWater(String(profile.target_water_ml))

    if (profile.manual_targets) {
      setTargetMode('manual')
      if (profile.target_protein_g) setManualProtein(String(Math.round(profile.target_protein_g)))
      if (profile.target_carbs_g) setManualCarbs(String(Math.round(profile.target_carbs_g)))
      if (profile.target_fat_g) setManualFat(String(Math.round(profile.target_fat_g)))
    } else {
      setTargetMode('auto')
    }
  }, [profile])

  // ── Populate weight from latest log ──
  const { data: weightLogs = [] } = useQuery({
    queryKey: ['logs', 'weight', 7],
    queryFn: () => api.logs.weight.list(7),
  })
  useEffect(() => {
    if (weightLogs.length > 0 && !weightKg) {
      setWeightKg(String(weightLogs[weightLogs.length - 1].weight_kg))
    }
  }, [weightLogs])

  // ── Live preview for manual mode ──
  const manualCalories =
    targetMode === 'manual'
      ? Math.round(
          (parseFloat(manualProtein) || 0) * 4 +
          (parseFloat(manualCarbs) || 0) * 4 +
          (parseFloat(manualFat) || 0) * 9
        )
      : 0

  // ── Save ──
  const saveMutation = useMutation({
    mutationFn: () =>
      api.profile.update({
        dob: dob || null,
        sex,
        height_cm: heightCm ? parseFloat(heightCm) : null,
        weight_kg: weightKg ? parseFloat(weightKg) : null,
        activity_level: activity,
        goal_kcal_delta: goalDelta,
        diet_type: dietType,
        manual_targets: targetMode === 'manual',
        target_protein_g: targetMode === 'manual' && manualProtein ? parseFloat(manualProtein) : null,
        target_carbs_g: targetMode === 'manual' && manualCarbs ? parseFloat(manualCarbs) : null,
        target_fat_g: targetMode === 'manual' && manualFat ? parseFloat(manualFat) : null,
        target_calories: targetMode === 'manual' ? manualCalories : null,
        target_water_ml: targetWater ? parseInt(targetWater) : 2000,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      // If auto-calculated, sync values back to manual fields for display
      if (!data.manual_targets && data.target_protein_g) {
        setManualProtein(String(Math.round(data.target_protein_g)))
        setManualCarbs(String(Math.round(data.target_carbs_g)))
        setManualFat(String(Math.round(data.target_fat_g)))
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#71717a' }}>Loading…</div>
  }

  return (
    <div
      data-page="profile"
      style={{ maxWidth: 480, margin: '0 auto', padding: '1rem 1rem 5rem' }}
    >
      <h1 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.25rem' }}>Profile</h1>
      {authUser && (
        <p style={{ fontSize: '0.8rem', color: '#71717a', marginBottom: '1.5rem' }}>
          {authUser.email}
        </p>
      )}

      {/* ── Body stats ── */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={sectionHeading}>Body stats</h2>

        <Field label="Date of birth">
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            style={{
              width: '100%',
              background: '#27272a',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              color: '#fafafa',
              fontSize: '0.9rem',
              padding: '0.55rem 0.75rem',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </Field>

        <Field label="Sex">
          <SegmentedControl
            options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]}
            value={sex}
            onChange={setSex}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="Height (cm)">
            <NumberInput value={heightCm} onChange={setHeightCm} placeholder="175" min="0" />
          </Field>
          <Field label="Weight (kg)">
            <NumberInput value={weightKg} onChange={setWeightKg} placeholder="80" min="0" />
          </Field>
        </div>
      </section>

      {/* ── Activity ── */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={sectionHeading}>Activity level</h2>
        <PickerList options={ACTIVITY_OPTIONS} value={activity} onChange={setActivity} />
      </section>

      {/* ── Targets ── */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={sectionHeading}>Daily targets</h2>

        <SegmentedControl
          options={[
            { value: 'auto',   label: 'TDEE (auto-calculate)' },
            { value: 'manual', label: 'Manual' },
          ]}
          value={targetMode}
          onChange={(v) => setTargetMode(v as 'auto' | 'manual')}
        />

        {/* ── Auto mode ── */}
        {targetMode === 'auto' && (
          <div style={{ marginTop: '1rem' }}>
            <Field label="Goal">
              <PickerList options={GOAL_OPTIONS} value={goalDelta} onChange={setGoalDelta} />
            </Field>

            <Field label="Diet type">
              <PickerList options={DIET_OPTIONS} value={dietType} onChange={setDietType} />
            </Field>

            {!heightCm || !weightKg || !dob ? (
              <div
                style={{
                  marginTop: '0.75rem',
                  background: '#1c1a14',
                  border: '1px solid #3f3a20',
                  borderRadius: 8,
                  padding: '0.6rem 0.75rem',
                  fontSize: '0.8rem',
                  color: '#a3a070',
                }}
              >
                Enter height, weight, and date of birth above to see your calculated targets.
              </div>
            ) : null}
          </div>
        )}

        {/* ── Manual mode ── */}
        {targetMode === 'manual' && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#71717a', marginBottom: '0.75rem' }}>
              Set your macro targets directly. Calories are calculated automatically.
            </p>

            {/* Preset loader */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem',
              }}
            >
              <span style={{ fontSize: '0.72rem', color: '#71717a', flexShrink: 0 }}>
                Load preset:
              </span>
              {MANUAL_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  title={preset.desc}
                  onClick={() => {
                    if (preset.value === 'psmf') {
                      const wkg = parseFloat(weightKg)
                      if (!wkg) return
                      setManualProtein(String(Math.round(wkg * 2.2)))
                      setManualCarbs('20')
                      setManualFat('20')
                    }
                  }}
                  disabled={preset.value === 'psmf' && !weightKg}
                  style={{
                    background: '#1a0f2e',
                    border: '1px solid #7c3aed',
                    borderRadius: 6,
                    color: '#a78bfa',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.25rem 0.6rem',
                    cursor: preset.value === 'psmf' && !weightKg ? 'not-allowed' : 'pointer',
                    opacity: preset.value === 'psmf' && !weightKg ? 0.45 : 1,
                  }}
                >
                  {preset.label}
                </button>
              ))}
              {!weightKg && (
                <span style={{ fontSize: '0.68rem', color: '#52525b' }}>
                  (enter weight first)
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem', marginBottom: '0.5rem' }}>
              <Field label="Protein (g)">
                <NumberInput value={manualProtein} onChange={setManualProtein} placeholder="150" min="0" />
              </Field>
              <Field label="Carbs (g)">
                <NumberInput value={manualCarbs} onChange={setManualCarbs} placeholder="100" min="0" />
              </Field>
              <Field label="Fat (g)">
                <NumberInput value={manualFat} onChange={setManualFat} placeholder="60" min="0" />
              </Field>
            </div>

            {(manualProtein || manualCarbs || manualFat) && (
              <MacroPreview
                calories={manualCalories}
                protein={parseFloat(manualProtein) || 0}
                carbs={parseFloat(manualCarbs) || 0}
                fat={parseFloat(manualFat) || 0}
                label="Calculated daily total"
              />
            )}
          </div>
        )}

        {/* ── Water (both modes) ── */}
        <div style={{ marginTop: '1rem' }}>
          <Field label="Water target (ml)">
            <NumberInput value={targetWater} onChange={setTargetWater} placeholder="2000" min="0" />
          </Field>
        </div>
      </section>

      {/* ── Save ── */}
      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        style={{
          width: '100%',
          background: saved ? '#166534' : '#22d3ee',
          border: 'none',
          borderRadius: 10,
          color: saved ? '#bbf7d0' : '#09090b',
          fontSize: '0.95rem',
          fontWeight: 700,
          padding: '0.75rem',
          cursor: saveMutation.isPending ? 'default' : 'pointer',
          transition: 'background 0.3s',
        }}
      >
        {saveMutation.isPending ? 'Saving…' : saved ? '✓ Saved' : 'Save profile'}
      </button>
    </div>
  )
}

const sectionHeading: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#71717a',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.75rem',
}
